import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
} from "antd";
import {
  DollarOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  StopOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import "./createReward.css";
import "./updateReward.css";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const { Option } = Select;

const REWARD_TYPES = [
  {
    value: "DISCOUNT_ON_SALE",
    title: "Sale Discount",
    description: "Give a fixed or percentage discount on the entire order.",
    Icon: DollarOutlined,
  },
  {
    value: "FREE_ITEM",
    title: "Free Item",
    description: "Let customers redeem points for a free menu item.",
    Icon: GiftOutlined,
  },
];

const UpdateReward = () => {
  const navigate = useNavigate();
  const { rewardId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [form] = Form.useForm();
  const [rewardType, setRewardType] = useState(null);
  const [itemLookup, setItemLookup] = useState(() => new Map());
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFreeItem, setSelectedFreeItem] = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [isFreeItemRemoved, setIsFreeItemRemoved] = useState(false);
  const [statusEnabled, setStatusEnabled] = useState(true);

  const watchedRewardName = Form.useWatch("RewardName", form);
  const watchedDiscountType = Form.useWatch("DiscountType", form);
  const watchedAmount = Form.useWatch("Amount", form);
  const watchedRequiredPoints = Form.useWatch("RequiredPoints", form);

  // Helpers for nicer item/category display
  const getSelectedItemObjects = () =>
    selectedItems
      .map((id) => {
        if (typeof id === "object" && id !== null) return id;
        return itemLookup.get(String(id)) || {
          MenuItem_ID: id,
          menuItem_ID: id,
          MenuItem_Name: `Item ${id}`,
        };
      })
      .filter(Boolean);

  const getSelectedCategoryObjects = () =>
    selectedCategories
      .map((id) => categories.find((c) => c.RootCat_ID === id))
      .filter(Boolean);

  const removeSelectedItem = (id) => {
    setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
  };

  const removeSelectedCategory = (id) => {
    setSelectedCategories((prev) => prev.filter((catId) => catId !== id));
  };

  // Modal state management
  const [modals, setModals] = useState({
    showCategoryModal: false,
    showItemModal: false,
    showFreeItemModal: false,
  });

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;

  useEffect(() => {
    loadCategories();
    if (rewardId) {
      loadReward(rewardId);
    }
  }, [rewardId]);

      // Populate form when editingReward data is loaded
  useEffect(() => {
    if (editingReward) {
      // Handle both camelCase and PascalCase field names from API
      const freeItemID = editingReward.freeItemID || editingReward.FreeItemID;
      
      // Handle status - convert to boolean if needed
      let statusValue = true; // default
      if (editingReward.status !== undefined) {
        statusValue = editingReward.status === true || editingReward.status === 'true' || editingReward.status === 1;
      } else if (editingReward.Status !== undefined) {
        statusValue = editingReward.Status === true || editingReward.Status === 'true' || editingReward.Status === 1;
      }
      
      console.log('Setting form values. Status value:', statusValue, 'Type:', typeof statusValue);
      console.log('Reward status fields:', { 
        status: editingReward.status, 
        Status: editingReward.Status,
        statusType: typeof editingReward.status,
        StatusType: typeof editingReward.Status
      });
      
      form.setFieldsValue({
        Reward_ID: editingReward.reward_ID || editingReward.Reward_ID,
        CompID: editingReward.compID || editingReward.CompID,
        RewardName: editingReward.rewardName || editingReward.RewardName,
        RewardType: editingReward.rewardType || editingReward.RewardType,
        DiscountType: editingReward.discountType || editingReward.DiscountType,
        Amount: editingReward.amount || editingReward.Amount,
        RequiredPoints: editingReward.requiredPoints || editingReward.RequiredPoints,
        FreeItemID: freeItemID,
        Status: statusValue,
      });
      setStatusEnabled(statusValue);
      setRewardType(editingReward.rewardType || editingReward.RewardType);

      // Set conditions
      const rewardType = editingReward.rewardType || editingReward.RewardType;
      if (editingReward.conditions && editingReward.conditions.length > 0) {
        if (rewardType === 'DISCOUNT_ON_ITEMS') {
          setSelectedItems(editingReward.conditions.map(c => c.itemORCategoryID || c.ItemORCategoryID));
        } else if (rewardType === 'DISCOUNT_ON_CATEGORY') {
          setSelectedCategories(editingReward.conditions.map(c => c.itemORCategoryID || c.ItemORCategoryID));
        }
      } else {
        // Reset selections if no conditions
        setSelectedItems([]);
        setSelectedCategories([]);
      }
    }
  }, [editingReward, form]);

  // Set free item when editingReward has freeItemID (hydrate via ByIds if needed)
  useEffect(() => {
    if (editingReward && !selectedFreeItem && !isFreeItemRemoved) {
      const rewardType = editingReward.rewardType || editingReward.RewardType;
      const freeItemID = editingReward.freeItemID || editingReward.FreeItemID;

      if (rewardType === "FREE_ITEM" && freeItemID) {
        if (editingReward.freeItem || editingReward.FreeItem) {
          const freeItemObj = editingReward.freeItem || editingReward.FreeItem;
          setSelectedFreeItem(freeItemObj);
          mergeItemLookup([freeItemObj]);
          const itemId =
            freeItemObj.MenuItem_ID || freeItemObj.menuItem_ID || freeItemObj.id;
          form.setFieldsValue({ FreeItemID: itemId });
        } else {
          hydrateItemsByIds([freeItemID]).then(() => {
            const cached = itemLookup.get(String(freeItemID));
            if (cached) {
              setSelectedFreeItem(cached);
              form.setFieldsValue({ FreeItemID: freeItemID });
            } else {
              setSelectedFreeItem({
                MenuItem_ID: freeItemID,
                menuItem_ID: freeItemID,
                MenuItem_Name: `Item ${freeItemID}`,
              });
              form.setFieldsValue({ FreeItemID: freeItemID });
            }
          });
        }
      } else if (rewardType !== "FREE_ITEM") {
        setSelectedFreeItem(null);
      } else if (!freeItemID) {
        setSelectedFreeItem(null);
      }
    }
  }, [editingReward, form, isFreeItemRemoved, selectedFreeItem]);

  // Hydrate labels for selected discount items
  useEffect(() => {
    if (selectedItems?.length) {
      hydrateItemsByIds(selectedItems);
    }
  }, [selectedItems]);

  // Modal management
  const openModal = (modalName) => {
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = key === modalName;
      });
      return newModals;
    });
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
  };

  const closeAllModals = () => {
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = false;
      });
      return newModals;
    });
    document.body.style.overflow = 'unset';
    document.body.classList.remove('modal-open');
  };

  // Handle selector submissions
  const handleSelectorSubmit = (key) => (selected) => {
    if (key === "selectedCategories") {
      setSelectedCategories(selected);
    } else if (key === "selectedItems") {
      const list = Array.isArray(selected) ? selected : [];
      mergeItemLookup(list.filter((p) => typeof p === "object"));
      const ids = list.map((p) =>
        typeof p === "object" ? p.menuItem_ID ?? p.MenuItem_ID ?? p.id : p
      );
      setSelectedItems(ids);
    }
  };

  // Handle free item selection (single item only)
  const handleFreeItemSelect = (selectedProducts) => {
    const productArray = Array.isArray(selectedProducts) ? selectedProducts : [];
    const first = productArray.length > 0 ? productArray[0] : null;
    setIsFreeItemRemoved(false);

    if (first) {
      const selectedItem =
        typeof first === "object"
          ? {
              ...first,
              MenuItem_ID: first.MenuItem_ID || first.menuItem_ID || first.id,
              MenuItem_Name:
                first.MenuItem_Name || first.menuItem_Name || first.name || "",
            }
          : {
              MenuItem_ID: first,
              menuItem_ID: first,
              MenuItem_Name: `Item ${first}`,
            };
      mergeItemLookup([selectedItem]);
      setSelectedFreeItem(selectedItem);
      form.setFieldsValue({ FreeItemID: selectedItem.MenuItem_ID });
      showSuccessToast("Free item selected successfully");
    } else {
      setSelectedFreeItem(null);
      form.setFieldsValue({ FreeItemID: null });
    }
    closeAllModals();
  };

  // Cleanup modal styles on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, []);

  const loadReward = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_SINGLE_REWARD}?compID=${CompID}&rewardID=${id}`
      );

      if (response.data.status) {
        const reward = response.data.data;
        console.log('Loaded reward:', reward);
        console.log('Reward Type:', reward.rewardType || reward.RewardType);
        console.log('FreeItemID:', reward.freeItemID || reward.FreeItemID);
        console.log('FreeItem object:', reward.freeItem || reward.FreeItem);
        console.log('Full reward object keys:', Object.keys(reward));
        setEditingReward(reward);
        // Reset the removed flag when loading a new reward
        setIsFreeItemRemoved(false);
      }
    } catch (error) {
      console.error("Error loading reward:", error);
      showErrorToast("Error loading reward details");
      navigate('/loyalty/rewards');
    } finally {
      setLoading(false);
    }
  };

  const mergeItemLookup = (list) => {
    if (!Array.isArray(list) || !list.length) return;
    setItemLookup((prev) => {
      const next = new Map(prev);
      list.forEach((item) => {
        const id = item.MenuItem_ID || item.menuItem_ID || item.id;
        if (id == null) return;
        next.set(String(id), {
          ...item,
          MenuItem_ID: id,
          menuItem_ID: id,
          MenuItem_Name:
            item.MenuItem_Name || item.menuItem_Name || item.name || `Item ${id}`,
        });
      });
      return next;
    });
  };

  const hydrateItemsByIds = async (ids) => {
    const unique = [
      ...new Set(
        (ids || [])
          .map((id) => (typeof id === "object" ? id.MenuItem_ID || id.menuItem_ID || id.id : id))
          .filter((id) => id != null && Number(id) > 0)
          .map(String)
      ),
    ];
    if (!CompID || !unique.length) return;
    try {
      const response = await axiosInstance.get(APIs.GET_MENU_ITEMS_BY_IDS, {
        params: { CompID, Ids: unique.join(",") },
      });
      const data = response.data?.data || [];
      if (Array.isArray(data)) mergeItemLookup(data);
    } catch (error) {
      console.error("Error hydrating items:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get(
        `${APIs.GET_CATEGORIES}?compID=${CompID}`
      );
      if (response.data.status || response.data.Status) {
        setCategories(response.data.data || response.data.Data || []);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories([]);
    }
  };

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    const vals = form.getFieldsValue(true);
    return JSON.stringify({
      rewardType: rewardType || null,
      statusEnabled: Boolean(statusEnabled),
      form: vals,
      selectedItems,
      selectedCategories,
      freeItemId:
        selectedFreeItem?.MenuItem_ID ||
        selectedFreeItem?.menuItem_ID ||
        selectedFreeItem?.id ||
        null,
      isFreeItemRemoved: Boolean(isFreeItemRemoved),
      watchedRewardName,
      watchedDiscountType,
      watchedAmount,
      watchedRequiredPoints,
    });
  }, [form, rewardType, statusEnabled, selectedItems, selectedCategories, selectedFreeItem, isFreeItemRemoved, watchedRewardName, watchedDiscountType, watchedAmount, watchedRequiredPoints]);

  useEffect(() => {
    if (loading) return;
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [loading, getFormSnapshot]);

  const isDirty =
    !loading &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return false;
    }

    try {
      setSaving(true);

      // Validate that items/categories are selected for ITEM/CATEGORY rules
      if (values.RewardType === 'DISCOUNT_ON_ITEMS' && selectedItems.length === 0) {
        showErrorToast('Please select at least one item for the loyalty rule');
        return false;
      }
      if (values.RewardType === 'DISCOUNT_ON_CATEGORY' && selectedCategories.length === 0) {
        showErrorToast('Please select at least one category for the loyalty rule');
        return false;
      }
      if (values.RewardType === 'FREE_ITEM' && !selectedFreeItem && !values.FreeItemID) {
        showErrorToast('Please select a free item for this reward');
        return false;
      }

      let conditions = [];

      // Add selected items/categories as conditions for ITEM/CATEGORY rules
      if (values.RewardType === 'DISCOUNT_ON_ITEMS') {
        conditions = selectedItems.map((id) => ({
          ItemORCategoryID:
            typeof id === "object" ? id.menuItem_ID ?? id.MenuItem_ID ?? id.id : id,
        }));
      } else if (values.RewardType === 'DISCOUNT_ON_CATEGORY') {
        conditions = selectedCategories.map(id => ({ ItemORCategoryID: id }));
      }

      // Get FreeItemID - prioritize selectedFreeItem, fallback to form value
      // Always include FreeItemID in payload (null if not applicable)
      let freeItemID = null;
      if (values.RewardType === 'FREE_ITEM') {
        if (selectedFreeItem) {
          freeItemID = selectedFreeItem.MenuItem_ID || selectedFreeItem.menuItem_ID || selectedFreeItem.id;
        } else if (values.FreeItemID) {
          freeItemID = values.FreeItemID;
        } else if (editingReward?.freeItemID || editingReward?.FreeItemID) {
          // Keep existing FreeItemID if form has it
          freeItemID = editingReward.freeItemID || editingReward.FreeItemID;
        }
      } else {
        // If reward type is not FREE_ITEM, explicitly set to null to clear it
        freeItemID = null;
      }

      // Ensure FreeItemID is always included in payload, even if null
      const payload = {
        Reward_ID: editingReward?.Reward_ID || editingReward?.reward_ID,
        CompID: CompID,
        RewardName: values.RewardName,
        RewardType: values.RewardType,
        DiscountType: values.DiscountType || null,
        Amount: values.Amount || null,
        RequiredPoints: values.RequiredPoints,
        FreeItemID: freeItemID !== undefined ? freeItemID : null, // Always explicitly include, even if null
        Status: values.Status !== undefined ? values.Status : true,
        Conditions: conditions
      };

      console.log('Update payload:', JSON.stringify(payload, null, 2));
      console.log('FreeItemID in payload:', payload.FreeItemID, 'Type:', typeof payload.FreeItemID);

      const response = await axiosInstance.post(
        `${APIs.UPSERT_REWARD}`,
        payload
      );

      if (response.data.status) {
        showSuccessToast("Reward updated successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate('/loyalty/rewards');
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update reward");
        return false;
      }
    } catch (error) {
      console.error("Error updating reward:", error);
      showErrorToast("Error updating reward");
      return false;
    } finally {
      setSaving(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const handleRewardTypeChange = (value) => {
    setRewardType(value);
    setSelectedItems([]);
    setSelectedCategories([]);
    setSelectedFreeItem(null);
    setIsFreeItemRemoved(false);
    form.setFieldsValue({
      RewardType: value,
      DiscountType: null,
      Amount: null,
      FreeItemID: null,
    });
  };

  const handleStatusToggle = (checked) => {
    setStatusEnabled(checked);
    form.setFieldsValue({ Status: checked });
  };

  const selectedTypeMeta = REWARD_TYPES.find((t) => t.value === rewardType);

  const getPreviewText = () => {
    if (!rewardType) return "Select a reward type to see how redemption will work.";
    if (rewardType === "DISCOUNT_ON_SALE") {
      if (!watchedDiscountType || !watchedAmount) {
        return "Choose discount type and amount to preview the sale discount.";
      }
      const amountLabel =
        watchedDiscountType === "PERCENTAGE"
          ? `${watchedAmount}% off`
          : `$${watchedAmount} off`;
      return `Customers redeem points for ${amountLabel} their entire order.`;
    }
    if (rewardType === "FREE_ITEM") {
      if (!selectedFreeItem) return "Select which menu item customers receive for free.";
      const name =
        selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name || "item";
      return `Customers redeem points for a free ${name}.`;
    }
    if (rewardType === "DISCOUNT_ON_ITEMS") {
      return "Customer gets discount on selected items.";
    }
    if (rewardType === "DISCOUNT_ON_CATEGORY") {
      return "Customer gets discount on selected categories.";
    }
    return "";
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/loyalty/rewards">Rewards Management</Link>
          </li>
          <li className="breadcrumb-item active">Update Reward</li>
        </ol>
        <h1 className="page-header mb-0">
          Update Reward
        </h1>
        <p className="text-muted">
          Update reward details and settings
        </p>
      </div>
      <SaveActions
        onCancel={() => navigate("/loyalty/rewards")}
        onSave={() => handleSubmit({ exitAfterSave: false })}
        onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
        saving={saving}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="p-3">
        <GridSectionLoader loading={true} minHeight="70vh" />
      </div>
    );
  }

  return (
    <div
      className={`update-reward-page create-reward-page ${isDarkMode ? "create-reward-page--dark" : ""}`}
      style={{ "--reward-accent": themeColor }}
    >
      {renderBreadcrumb()}

      <Form
        form={form}
        layout="vertical"
        onFinish={() => handleSubmit({ exitAfterSave: true })}
        initialValues={{
          Status: true,
        }}
      >
        <div
          className={`reward-config-shell ${isDarkMode ? "reward-config-shell--dark" : ""}`}
        >
          <div className="reward-config-intro">
            <h5 className="reward-config-intro__title">Update how customers redeem points</h5>
            <p className="reward-config-intro__text mb-0">
              Adjust the reward type, offer details, and availability for this loyalty reward.
            </p>
          </div>

          <div className="reward-config-section">
            <div className="reward-config-section__header">
              <span className="reward-config-step">1</span>
              <div>
                <h6 className="mb-0">Choose reward type</h6>
                <small className="text-muted">
                  Pick how customers will spend their loyalty points
                </small>
              </div>
            </div>

            <div
              className={`reward-type-grid ${isDarkMode ? "reward-type-grid--dark" : ""}`}
              role="radiogroup"
              aria-label="Reward type"
            >
              {REWARD_TYPES.map(({ value, title, description, Icon }) => {
                const isSelected = rewardType === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={`reward-type-card ${
                      isSelected ? "reward-type-card--selected" : ""
                    }`}
                    onClick={() => handleRewardTypeChange(value)}
                  >
                    <span className="reward-type-card__icon">
                      <Icon />
                    </span>
                    <span className="reward-type-card__body">
                      <span className="reward-type-card__title">{title}</span>
                      <span className="reward-type-card__desc">{description}</span>
                    </span>
                    {isSelected && (
                      <CheckCircleOutlined className="reward-type-card__check" />
                    )}
                  </button>
                );
              })}
            </div>
            <Form.Item
              name="RewardType"
              rules={[{ required: true, message: "Please select a reward type" }]}
              className="mb-0 reward-type-hidden-field"
            >
              <Select aria-hidden="true" tabIndex={-1}>
                {REWARD_TYPES.map(({ value, title }) => (
                  <Option key={value} value={value}>
                    {title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <div
              className={`reward-config-main ${isDarkMode ? "reward-config-main--dark" : ""}`}
            >
                <div className="reward-config-section__header mb-3">
                  <span className="reward-config-step">2</span>
                  <div>
                    <h6 className="mb-0">Configure reward</h6>
                    <small className="text-muted">
                      {rewardType
                        ? selectedTypeMeta?.description
                        : "Select a reward type above to continue"}
                    </small>
                  </div>
                </div>

                <Form.Item
                  name="RewardName"
                  label="Reward name"
                  rules={[{ required: true, message: "Please enter reward name" }]}
                  extra="Shown to staff and customers when redeeming."
                >
                  <Input
                    placeholder="e.g., $5 Off Entire Sale"
                    size="large"
                    className="reward-config-input"
                  />
                </Form.Item>

                {/* Discount Type and Amount for discount rewards */}
                {(rewardType === 'DISCOUNT_ON_SALE' ||
                  rewardType === 'DISCOUNT_ON_ITEMS' ||
                  rewardType === 'DISCOUNT_ON_CATEGORY') && (
                    <>
                      <Form.Item
                        name="DiscountType"
                        label="Discount Type"
                        rules={[{ required: true, message: "Please select discount type" }]}
                      >
                        <Select
                          placeholder="Select discount type"
                          getPopupContainer={trigger => trigger.parentNode}
                          dropdownStyle={{ zIndex: 1050 }}
                        >
                          <Option value="FIXED_AMOUNT">Fixed Amount ($)</Option>
                          <Option value="PERCENTAGE">Percentage (%)</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="Amount"
                        label="Discount Amount"
                        rules={[{ required: true, message: "Please enter discount amount" }]}
                      >
                        <InputNumber
                          min={0.01}
                          step={0.01}
                          placeholder="5.00"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>

                      {/* Items/Categories selection for DISCOUNT_ON_ITEMS and DISCOUNT_ON_CATEGORY */}
                      {rewardType === 'DISCOUNT_ON_ITEMS' && (
                        <Form.Item label="Items Included in Reward">
                          <div
                            className="card p-3"
                            style={{
                              backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                              border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                              borderRadius: '12px',
                            }}
                          >
                            {getSelectedItemObjects().length > 0 ? (
                              <>
                                <div className="row row-cols-1 row-cols-md-2 g-3">
                                  {getSelectedItemObjects().map((item) => (
                                    <div className="col" key={item.MenuItem_ID}>
                                      <div
                                        className="d-flex justify-content-between align-items-center"
                                        style={{
                                          background: isDarkMode ? '#111827' : '#f8f9fa',
                                          borderRadius: '10px',
                                          padding: '10px 14px',
                                          border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                          transition: 'all 0.2s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.borderColor = themeColor;
                                          e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}33`;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.borderColor = isDarkMode ? '#374151' : '#e9ecef';
                                          e.currentTarget.style.boxShadow = 'none';
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <div
                                            style={{
                                              width: '32px',
                                              height: '32px',
                                              borderRadius: '8px',
                                              background: isDarkMode ? '#111827' : '#ffffff',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              marginRight: '10px',
                                              border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                            }}
                                          >
                                            <i className="fa fa-box" style={{ fontSize: '14px', color: '#6c757d' }}></i>
                                          </div>
                                          <div>
                                            <div
                                              style={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: isDarkMode ? '#e5e7eb' : '#111827',
                                              }}
                                            >
                                              {item.MenuItem_Name}
                                            </div>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeSelectedItem(item.MenuItem_ID)}
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#dc3545',
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#f8d7da';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                          }}
                                        >
                                          <i className="fa fa-times" style={{ fontSize: '12px' }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="d-flex justify-content-center mt-3">
                                  <button
                                    type="button"
                                    onClick={() => openModal('showItemModal')}
                                    style={{
                                      background: themeColor,
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '8px 18px',
                                      color: 'white',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                                    Manage Items
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-3">
                                <div
                                  style={{
                                    width: '52px',
                                    height: '52px',
                                    background: isDarkMode ? '#111827' : '#f8f9fa',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px',
                                    border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                  }}
                                >
                                  <i className="fa fa-box" style={{ fontSize: '20px', color: '#6c757d' }}></i>
                                </div>
                                <p
                                  style={{
                                    color: '#6c757d',
                                    fontSize: '14px',
                                    marginBottom: '14px',
                                  }}
                                >
                                  No items selected. Choose which items this reward applies to.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => openModal('showItemModal')}
                                  style={{
                                    background: themeColor,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 18px',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    margin: '0 auto',
                                  }}
                                >
                                  <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                                  Select Items
                                </button>
                              </div>
                            )}
                          </div>
                        </Form.Item>
                      )}

                      {rewardType === 'DISCOUNT_ON_CATEGORY' && (
                        <Form.Item label="Categories Included in Reward">
                          <div
                            className="card p-3"
                            style={{
                              backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                              border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                              borderRadius: '12px',
                            }}
                          >
                            {getSelectedCategoryObjects().length > 0 ? (
                              <>
                                <div className="row row-cols-1 row-cols-md-2 g-3">
                                  {getSelectedCategoryObjects().map((category) => (
                                    <div className="col" key={category.RootCat_ID}>
                                      <div
                                        className="d-flex justify-content-between align-items-center"
                                        style={{
                                          background: isDarkMode ? '#111827' : '#f8f9fa',
                                          borderRadius: '10px',
                                          padding: '10px 14px',
                                          border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                          transition: 'all 0.2s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.borderColor = themeColor;
                                          e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}33`;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.borderColor = isDarkMode ? '#374151' : '#e9ecef';
                                          e.currentTarget.style.boxShadow = 'none';
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <div
                                            style={{
                                              width: '32px',
                                              height: '32px',
                                              borderRadius: '8px',
                                              background: isDarkMode ? '#111827' : '#ffffff',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              marginRight: '10px',
                                              border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                            }}
                                          >
                                            <i className="fa fa-tags" style={{ fontSize: '14px', color: '#6c757d' }}></i>
                                          </div>
                                          <div>
                                            <div
                                              style={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: isDarkMode ? '#e5e7eb' : '#111827',
                                              }}
                                            >
                                              {category.RootCat_Name}
                                            </div>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeSelectedCategory(category.RootCat_ID)}
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#dc3545',
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#f8d7da';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                          }}
                                        >
                                          <i className="fa fa-times" style={{ fontSize: '12px' }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="d-flex justify-content-center mt-3">
                                  <button
                                    type="button"
                                    onClick={() => openModal('showCategoryModal')}
                                    style={{
                                      background: themeColor,
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '8px 18px',
                                      color: 'white',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                                    Manage Categories
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-3">
                                <div
                                  style={{
                                    width: '52px',
                                    height: '52px',
                                    background: isDarkMode ? '#111827' : '#f8f9fa',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px',
                                    border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                  }}
                                >
                                  <i className="fa fa-tags" style={{ fontSize: '20px', color: '#6c757d' }}></i>
                                </div>
                                <p
                                  style={{
                                    color: '#6c757d',
                                    fontSize: '14px',
                                    marginBottom: '14px',
                                  }}
                                >
                                  No categories selected. Choose which categories this reward applies to.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => openModal('showCategoryModal')}
                                  style={{
                                    background: themeColor,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 18px',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    margin: '0 auto',
                                  }}
                                >
                                  <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                                  Select Categories
                                </button>
                              </div>
                            )}
                          </div>
                        </Form.Item>
                      )}
                    </>
                  )}

                {/* Free Item selection */}
                {rewardType === 'FREE_ITEM' && (
                  <Form.Item
                    name="FreeItemID"
                    label="Free Item"
                    rules={[{ required: true, message: "Please select a free item" }]}
                  >
                    <div
                      className="card p-3"
                      style={{
                        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                        border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                        borderRadius: '12px',
                      }}
                    >
                      {selectedFreeItem ? (
                        <div
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            background: isDarkMode ? '#111827' : '#f8f9fa',
                            borderRadius: '10px',
                            padding: '16px',
                            border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {/* Item Image */}
                            <div
                              style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '10px',
                                background: isDarkMode ? '#111827' : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '16px',
                                border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                                overflow: 'hidden',
                              }}
                            >
                              {(selectedFreeItem.menuItem_Image || selectedFreeItem.MenuItem_Image) ? (
                                <img
                                  src={selectedFreeItem.menuItem_Image || selectedFreeItem.MenuItem_Image}
                                  alt={selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <i className="fa fa-box" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                              )}
                            </div>
                            {/* Item Details */}
                            <div>
                              <div
                                style={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: isDarkMode ? '#e5e7eb' : '#111827',
                                  marginBottom: '4px',
                                }}
                              >
                                {selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name}
                              </div>
                              {(selectedFreeItem.menuItem_SalePrice || selectedFreeItem.MenuItem_SalePrice) && (
                                <div
                                  style={{
                                    fontSize: '14px',
                                    color: '#6c757d',
                                    fontWeight: 500,
                                  }}
                                >
                                  ${parseFloat(selectedFreeItem.menuItem_SalePrice || selectedFreeItem.MenuItem_SalePrice || 0).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFreeItem(null);
                              setIsFreeItemRemoved(true);
                              form.setFieldsValue({ FreeItemID: null });
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#dc3545',
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f8d7da';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <i className="fa fa-times" style={{ fontSize: '14px' }}></i>
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              background: isDarkMode ? '#111827' : '#f8f9fa',
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto 16px',
                              border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                            }}
                          >
                            <i className="fa fa-gift" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                          </div>
                          <p
                            style={{
                              color: '#6c757d',
                              fontSize: '14px',
                              marginBottom: '16px',
                            }}
                          >
                            No free item selected. Choose which item customers will receive for free.
                          </p>
                          <button
                            type="button"
                            onClick={() => openModal('showFreeItemModal')}
                            style={{
                              background: themeColor,
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 20px',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              margin: '0 auto',
                            }}
                          >
                            <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                            Select Free Item
                          </button>
                        </div>
                      )}
                    </div>
                  </Form.Item>
                )}

                <Form.Item
                  name="RequiredPoints"
                  label="Points required"
                  rules={[{ required: true, message: "Please enter points required" }]}
                  extra="Loyalty points a customer must spend to redeem this reward."
                >
                  <InputNumber
                    min={1}
                    placeholder="100"
                    size="large"
                    className="w-100 reward-config-input"
                  />
                </Form.Item>
            </div>
          </Col>

          <Col xs={24} lg={8}>
            <div
              className={`reward-status-panel ${isDarkMode ? "reward-status-panel--dark" : ""}`}
            >
              <div className="reward-status-panel__header">
                <CheckCircleOutlined />
                <div>
                  <h5 className="mb-0">Status &amp; preview</h5>
                  <small>Control whether this reward is available</small>
                </div>
              </div>

              <Form.Item
                name="Status"
                label="Reward status"
                valuePropName="checked"
                className="mb-3"
              >
                <ToggleSwitch
                  isChecked={statusEnabled}
                  onToggle={handleStatusToggle}
                  label={
                    statusEnabled ? (
                      <span className="text-success">
                        <CheckCircleOutlined className="me-1" />
                        Active
                      </span>
                    ) : (
                      <span className="text-muted">
                        <StopOutlined className="me-1" />
                        Inactive
                      </span>
                    )
                  }
                  name="Status"
                />
              </Form.Item>

              <div className="reward-status-preview">
                <div className="reward-status-preview__label">
                  <InfoCircleOutlined className="me-1" />
                  How it works
                </div>
                <p className="reward-status-preview__text mb-2">{getPreviewText()}</p>
                <div className="reward-status-preview__meta">
                  {rewardType && (
                    <span className="reward-status-chip">
                      {selectedTypeMeta?.title || rewardType}
                    </span>
                  )}
                  {watchedRequiredPoints ? (
                    <span className="reward-status-chip reward-status-chip--muted">
                      {watchedRequiredPoints} pts
                    </span>
                  ) : null}
                  {watchedRewardName ? (
                    <span className="reward-status-chip reward-status-chip--muted">
                      {watchedRewardName}
                    </span>
                  ) : null}
                  {!statusEnabled && (
                    <span className="reward-status-chip reward-status-chip--warn">
                      Not available yet
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </Form>

      {/* Category Selection Modal */}
      <CategorySelector
        selectedCategories={selectedCategories}
        setSelectedCategories={(updater) =>
          setSelectedCategories(typeof updater === 'function' ? updater(selectedCategories) : updater)
        }
        showModal={modals.showCategoryModal}
        setShowModal={(val) =>
          val ? openModal('showCategoryModal') : closeAllModals()
        }
        onSubmit={handleSelectorSubmit("selectedCategories")}
        setLoading={setSelectorLoading}
        hideDisplay={true}
      />

      {/* Item Selection Modal */}
      <ProductMultiSelectionModal
        selectedProducts={selectedItems}
        setSelectedProducts={(products) =>
          handleSelectorSubmit("selectedItems")(products)
        }
        showModal={modals.showItemModal}
        setShowModal={(val) =>
          val ? openModal('showItemModal') : closeAllModals()
        }
        onSubmit={handleSelectorSubmit("selectedItems")}
        setLoading={setSelectorLoading}
        showButton={true}
      />

      {/* Free Item Selection Modal - Single Item Only */}
      <ProductMultiSelectionModal
        selectedProducts={selectedFreeItem ? [selectedFreeItem.MenuItem_ID || selectedFreeItem.menuItem_ID] : []}
        setSelectedProducts={handleFreeItemSelect}
        showModal={modals.showFreeItemModal}
        setShowModal={(val) =>
          val ? openModal('showFreeItemModal') : closeAllModals()
        }
        onSubmit={handleFreeItemSelect}
        setLoading={setSelectorLoading}
        showButton={true}
        maxSelection={1}
      />
    </div>
  );
};

export default UpdateReward;